import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { toast } from "react-toastify";

import { DEFAULT_ERROR_MESSAGE } from "~/constants/common";

import { sanitizeMessage } from "./sanitizeHtml";

interface IBackendError {
  status: number;
  data?: {
    errors: string | string[];
    message?: string | string[];
  };
}

const isFetchBaseQueryError = (
  error: unknown
): error is FetchBaseQueryError => {
  return typeof error === "object" && error !== null && "status" in error;
};

const isBackendError = (error: FetchBaseQueryError): error is IBackendError => {
  return "data" in error;
};

const isRequestCancelledError = (error: unknown): boolean => {
  if (error == null || typeof error !== "object") return false;

  const err = error as Record<string, unknown>;

  if (err.name === "AbortError") return true;
  if (err.code === "ABORT_ERR") return true;

  const msg = err.message;
  if (typeof msg === "string" && /aborted|cancelled|canceled/i.test(msg)) {
    return true;
  }

  if (isFetchBaseQueryError(error)) {
    if (
      "error" in error &&
      typeof error.error === "string" &&
      /aborted|cancelled|canceled/i.test(error.error)
    ) {
      return true;
    }
  }

  return false;
};

const extractStringMessage = (
  value: string,
  defaultMessage: string
): string => {
  return sanitizeMessage(value, defaultMessage);
};

const extractArrayMessages = (
  values: string[],
  defaultMessage: string
): string => {
  const sanitizedMessages = values
    .map((value) => sanitizeMessage(String(value), defaultMessage))
    .filter((msg) => msg !== defaultMessage);

  return sanitizedMessages.length > 0
    ? sanitizedMessages.join("\n")
    : defaultMessage;
};

export const handleError = (
  error: unknown,
  defaultErrorMessage = DEFAULT_ERROR_MESSAGE
): void => {
  if (isRequestCancelledError(error)) return;

  let message = defaultErrorMessage;

  if (isFetchBaseQueryError(error)) {
    if ("error" in error && typeof error.error === "string") {
      message = extractStringMessage(error.error, defaultErrorMessage);
    }

    if (typeof error.data === "string") {
      message = extractStringMessage(error.data, defaultErrorMessage);
    }

    if (isBackendError(error)) {
      const { errors, message: beMessage } = error.data || {};

      if (beMessage) {
        if (typeof beMessage === "string") {
          message = extractStringMessage(beMessage, defaultErrorMessage);
        } else if (Array.isArray(beMessage) && beMessage.length > 0) {
          message = extractArrayMessages(beMessage, defaultErrorMessage);
        }
      } else if (errors) {
        if (typeof errors === "string") {
          message = extractStringMessage(errors, defaultErrorMessage);
        } else if (Array.isArray(errors) && errors.length > 0) {
          message = extractArrayMessages(errors, defaultErrorMessage);
        }
      }
    }
  }

  toast.error(message);
  console.error(error);
};
